import type { PaymentStatus } from '../api/types';
import type { Tone } from '../ui';

/**
 * How a Consignment Invoice's payable is *shown* — one tone and one string key per state, for every
 * screen that shows it, Admin and retailer alike.
 *
 * It is here rather than in each screen because the defect this file comes out of was two screens
 * describing one invoice differently: the retailer's page said "Payment pending · Total payable
 * USD 12,900" on a consignment where nothing was owed for the goods (SH-06, SH-13), while their
 * dashboard correctly said nothing was owed. The figure is now derived once on the server; the
 * words are chosen once here.
 */
export const PAYABLE_TONE: Record<PaymentStatus, Tone> = {
  /* Not a shade of unpaid: nothing is payable on this despatch at all. Muted, because there is
     nothing for anyone to act on — "pending" on a zero payable is how a retailer gets chased for
     nothing, and `'0.0000'` is a truthy string. */
  NOT_PAYABLE: 'lapse',
  PENDING: 'us',
  PART_PAID: 'run',
  PAID: 'done',
};

/** The i18n key for a payable state. `despatch.*` is shared by both portals (see `en.ts`). */
export const payableStatusKey = (status: PaymentStatus): string => `despatch.status${status}`;

/** `'0.0000'` is a truthy string, so zero-ness is a test on the digits — never on the value. */
export const isZeroAmount = (value: string | null | undefined): boolean =>
  typeof value === 'string' && /^-?0+(\.0+)?$/.test(value.trim());
